import { Box, Button, FileInput, Image, Layer, Spinner, Text } from 'grommet';
import { Close, Upload } from 'grommet-icons';
import { Asset, AssetType, TAssetId } from 'lib/api';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useProjectStore } from 'src/context';
import { borderRadiusSize } from 'src/theme';
import { AssetCloudImportButtons } from '../AssetCloudImportButtons';
import { Divider } from '../Divider';
import { FileUploader } from '../FileUploader';
import { ModalCard } from '../ModalCard';
import { ModalInfoCard } from '../ModalInfoCard';

export type AssetSelectorModalProps = {
  setShowModal: (show: boolean) => void;
  selectedAssetId?: TAssetId;
  handleAssetSelect: (asset: Asset) => Promise<void>;
};

export const AssetSelectorModal = ({ setShowModal, selectedAssetId, handleAssetSelect }: AssetSelectorModalProps) => {
  /** Context **/
  const projectStore = useProjectStore();

  /** State **/
  const [assetThumbnails, setAssetThumbnails] = useState<Map<string, string>>(new Map());
  const [uploading, setUploading] = useState(false);

  /** Computed **/
  const videoAssets = useMemo(
    () => (projectStore.currentProject?.assets || []).filter((asset) => asset.type === AssetType.Video),
    [projectStore.currentProject?.assets]
  );

  /** Methods **/

  const handleFileUpload = async (e?: React.ChangeEvent<HTMLInputElement>) => {
    const files = e?.target?.files;
    if (!files?.[0]) return;

    const file = files[0];
    const maxSize = 2 * 1024 * 1024 * 1024; // 2GB limit

    if (file.size > maxSize) {
      toast.error('File size exceeds 2GB limit');
      return;
    }

    setUploading(true);
    try {
      const asset = await projectStore.createAsset(file, AssetType.Video, '');
      if (!asset) {
        toast.error('Error uploading video');
      } else {
        toast.success('Video uploaded successfully');
        await handleAssetSelect(asset);
      }
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error uploading video');
    } finally {
      setUploading(false);
    }
  };

  /** Effects **/

  // Load thumbnails for video assets when selector opens
  useEffect(() => {
    if (videoAssets.length > 0) {
      const loadThumbnails = async () => {
        const thumbnailMap = new Map<string, string>();
        for (const asset of videoAssets) {
          if (asset.thumbnail_path) {
            const url = await projectStore.getAssetThumbnailUrl(asset.id);
            if (url) thumbnailMap.set(asset.id, url);
          }
        }
        setAssetThumbnails(thumbnailMap);
      };
      loadThumbnails();
    }
  }, [projectStore, videoAssets]);

  /** Render **/
  return (
    <ModalCard
      onClose={() => setShowModal(false)}
      title="Select or Upload Video"
      icon={<Upload size="24px" color="text" />}
      cardProps={{ width: 'large' }}
    >
      {/* <Box direction="row" justify="between" margin={{ bottom: 'medium' }} flex="grow">
        <IconButton icon={<Upload size="large" color="brand" />} />
        <IconButton icon={<Google size="large" color="brand" />} />
        <IconButton icon={<CloudUpload size="large" color="brand" />} />
        <IconButton icon={<Catalog size="large" color="brand" />} />
      </Box> */}
      <Box gap="medium" flex="grow">
        {/* Upload Section */}
        <ModalInfoCard
          title={
            <Text weight={500} size="small">
              Upload New Video
            </Text>
          }
        >
          <Box gap="medium" margin={{ vertical: 'xsmall' }} pad={{ horizontal: 'small' }}>
            <FileUploader
              handleFileUpload={handleFileUpload}
              uploading={uploading}
              maxFileSize="2GB"
              acceptedFormats={['.mp4', '.mov', '.avi', '.mkv', '.webm']}
              uploadType="video"
            />

            <Box>
              <Divider text="OR IMPORT FROM" />
            </Box>

            <AssetCloudImportButtons
              assetType={AssetType.Video}
              disabled={uploading}
              onImportSuccess={handleAssetSelect}
              onImportStart={() => setUploading(true)}
              onImportEnd={() => setUploading(false)}
              size="small"
              direction="row"
              showDivider={false}
            />
          </Box>
        </ModalInfoCard>

        <ModalInfoCard
          title={
            <Text weight={500} size="small">
              Or Select from Asset Library
            </Text>
          }
        >
          {uploading ? (
            <Box align="center" pad="large" gap="small">
              <Spinner />
              <Text size="small">Uploading video...</Text>
            </Box>
          ) : videoAssets.length === 0 ? (
            <Box align="center" pad="large" gap="small">
              <Text size="small" color="dark-2">
                No video assets in library yet
              </Text>
            </Box>
          ) : (
            <Box gap="small" overflow="auto" height="medium">
              {videoAssets.map((asset) => {
                const thumbnailUrl = assetThumbnails.get(asset.id);
                const duration = (
                  <>
                    {Math.floor((asset.duration_seconds ?? 0) / 60)}:
                    {String(Math.floor((asset.duration_seconds ?? 0) % 60)).padStart(2, '0')}
                  </>
                );
                return (
                  <Box
                    key={asset.id}
                    direction="row"
                    align="center"
                    gap="medium"
                    pad="small"
                    border={{ color: selectedAssetId === asset.id ? 'brand' : 'light-4' }}
                    round={borderRadiusSize}
                    background={selectedAssetId === asset.id ? 'light-1' : 'white'}
                    hoverIndicator="light-2"
                    onClick={() => handleAssetSelect(asset)}
                    style={{ cursor: 'pointer' }}
                  >
                    <Box background="light-3" round="xsmall" overflow="auto">
                      {thumbnailUrl ? (
                        <Image
                          src={thumbnailUrl}
                          alt={asset.name}
                          width="120px"
                          height="68px"
                          // style={{ width: '100%', height: '100%' }}
                        />
                      ) : (
                        <Box fill justify="center" align="center">
                          <Text size="xsmall" color="dark-2">
                            No preview
                          </Text>
                        </Box>
                      )}
                    </Box>
                    <Box flex>
                      <Text weight="bold">{asset.name}</Text>
                      <Text size="small" color="dark-2">
                        {asset.file_name}
                      </Text>
                      {asset.duration_seconds && (
                        <Text size="xsmall" color="dark-2">
                          Duration: {duration}
                        </Text>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </ModalInfoCard>
      </Box>
    </ModalCard>
  );

  return (
    <Layer onEsc={() => setShowModal(false)} onClickOutside={() => setShowModal(false)}>
      <Box pad="medium" gap="medium" width="large" height="large" overflow="auto">
        <Box direction="row" justify="between" align="center">
          <Text weight="bold">Select or Upload Video</Text>
          <Button icon={<Close />} onClick={() => setShowModal(false)} />
        </Box>

        {/* Upload Section */}
        <Box gap="small" border={{ side: 'bottom', color: 'light-4' }} pad={{ bottom: 'small' }}>
          <Text weight="bold" size="small">
            Upload New Video
          </Text>
          <FileInput
            name="file"
            onChange={handleFileUpload}
            disabled={uploading}
            multiple={false}
            accept=".mp4,.mov,.avi,.mkv,.webm"
            messages={{
              dropPrompt: 'Drop video here or click to browse',
              browse: 'Browse',
            }}
          />
          <Text size="xsmall" color="dark-5">
            Max file size: 2GB. Accepted formats: .mp4, .mov, .avi, .mkv, .webm
          </Text>
          <AssetCloudImportButtons
            assetType={AssetType.Video}
            disabled={uploading}
            onImportSuccess={handleAssetSelect}
            onImportStart={() => setUploading(true)}
            onImportEnd={() => setUploading(false)}
            size="small"
            direction="row"
          />
        </Box>

        {/* Existing Assets Section */}
        <Box gap="small">
          <Text weight="bold" size="small">
            Or Select from Asset Library
          </Text>
          {uploading ? (
            <Box align="center" pad="large" gap="small">
              <Spinner />
              <Text size="small">Uploading video...</Text>
            </Box>
          ) : videoAssets.length === 0 ? (
            <Box align="center" pad="large" gap="small">
              <Text size="small" color="dark-4">
                No video assets in library yet
              </Text>
            </Box>
          ) : (
            <Box gap="small" overflow="auto" height="medium">
              {videoAssets.map((asset) => {
                const thumbnailUrl = assetThumbnails.get(asset.id);
                return (
                  <Box
                    key={asset.id}
                    direction="row"
                    gap="medium"
                    pad="small"
                    border={{ color: selectedAssetId === asset.id ? 'brand' : 'light-4' }}
                    round="small"
                    background={selectedAssetId === asset.id ? 'light-1' : 'white'}
                    hoverIndicator="light-2"
                    onClick={() => handleAssetSelect(asset)}
                    style={{ cursor: 'pointer' }}
                    flex="grow"
                  >
                    <Box width="120px" height="68px" background="light-3" round="xsmall" overflow="hidden">
                      {thumbnailUrl ? (
                        <img
                          src={thumbnailUrl}
                          alt={asset.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Box fill justify="center" align="center">
                          <Text size="xsmall" color="dark-4">
                            No preview
                          </Text>
                        </Box>
                      )}
                    </Box>
                    <Box flex>
                      <Text weight="bold">{asset.name}</Text>
                      <Text size="small" color="dark-4">
                        {asset.file_name}
                      </Text>
                      {asset.duration_seconds && (
                        <Text size="xsmall" color="dark-5">
                          Duration: {Math.floor(asset.duration_seconds / 60)}:
                          {String(Math.floor(asset.duration_seconds % 60)).padStart(2, '0')}
                        </Text>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Layer>
  );
};
